import { gql } from '@apollo/client';
import { Pagination, Tooltip } from '@mui/material';
import { truncate } from 'lodash';
import { GetServerSideProps, NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import {
  CheckboxList,
  CheckboxListInput,
  CheckboxListItem,
  ConnectorCard,
  ConnectorCardContent,
  ConnectorCardDescription,
  ConnectorCardLogo,
  ConnectorCardTag,
  ConnectorCardTags,
  ConnectorCardTitle,
  Hero,
  HeroText,
  HeroTitle,
  PageContainer,
  SideNav,
  SideNavTitle,
} from '../../components/connectors/styled';
import { FOOTER_QUERY } from '../../components/footer';
import { Footer } from '../../components/footer';
import { HEADER_QUERY, Header } from '../../components/header';
import { Col, ResponsiveContainer, Row } from '../../components/responsive';
import { SelectInput } from '../../components/ui/sleectInput';
import { ContentStackClient } from '../../lib/contentstack';
import { getTheme } from '../../theme/getTheme';
import {
  ClibCategory,
  ClibConnector,
  ConnectorsPage,
} from '../../types/contentstack';
import { IPageProps } from '../../types/page';
import { renderRichText } from '../../utils/contentstack';
import {
  fetchConnectors,
  getCategoriesFromConnectors,
} from '../api/connectors';
import { OptimizedImage } from '../../components/ui/optimizedImage';

const PAGE_SIZE = 100;

type ConnectorsPageProps = IPageProps<ConnectorsPage> & {
  categories: ClibCategory[];
  connectors: ClibConnector[];
  totalConnectors: number;
};

const Page: NextPage<ConnectorsPageProps> = ({
  page,
  header,
  footer,
  categories,
  connectors,
  totalConnectors,
}) => {
  const router = useRouter();
  const theme = getTheme();

  return (
    <>
      <Header
        pageTitle={page?.title}
        pageDescription={page?.description}
        {...header}
      />

      <PageContainer>
        <Hero>
          <ResponsiveContainer>
            <HeroTitle>
              <strong>{page?.hero?.title}</strong>
            </HeroTitle>

            <HeroText>{page?.hero?.text}</HeroText>
          </ResponsiveContainer>
        </Hero>

        <ResponsiveContainer>
          <Row>
            <Col md={3}>
              <SideNav>
                <SideNavTitle className="desktop-only">Direction</SideNavTitle>
                <DirectionsList />

                <SideNavTitle
                  className="desktop-only"
                  style={{ marginTop: '40px' }}
                >
                  Category
                </SideNavTitle>
                <CategoriesList categories={categories} />
              </SideNav>
            </Col>
            <Col md={9}>
              <ConnectorsGrid connectors={connectors} />

              {totalConnectors > PAGE_SIZE && (
                <Pagination
                  count={parseInt(
                    ((totalConnectors + PAGE_SIZE - 1) / PAGE_SIZE).toString()
                  )}
                  page={Number(router.query.p || 1)}
                  variant="outlined"
                  shape="rounded"
                  size="large"
                  onChange={(_, p) =>
                    router.push({ query: { ...router.query, p } })
                  }
                  sx={{
                    marginTop: '50px',
                    display: 'flex',
                    justifyContent: 'center',
                    '& .MuiPaginationItem-root': {
                      margin: '0 5px',
                      color: theme.colors.lightTeal,
                      borderColor: theme.colors.teal,

                      ':hover': {
                        color: theme.colors.white,
                        backgroundColor: 'rgba(81,192,172, 0.15)',
                      },

                      '&.Mui-selected': {
                        color: theme.colors.white,
                        backgroundColor: theme.colors.teal,
                        pointerEvents: 'none',
                      },
                    },
                  }}
                />
              )}
            </Col>
          </Row>
        </ResponsiveContainer>
      </PageContainer>

      <Footer sections={footer?.sections} />
    </>
  );
};

const DirectionsList: React.FC = () => {
  const router = useRouter();
  const directions = ['Source', 'Destination'];
  const selectedDirections = parseSelectedDirections(router.query);

  return (
    <>
      <div className="mobile-only">
        <SelectInput
          label="Direction"
          onChange={(e) => {
            router.push({
              query: {
                d: [e.target.value],
                c: router.query.c,
              },
            });
          }}
        >
          <option value="">Select a direction</option>

          {directions?.map((item) => {
            return (
              <option key={item} value={item}>
                {item}
              </option>
            );
          })}
        </SelectInput>
      </div>

      <CheckboxList className="desktop-only">
        {directions?.map((item, idx) => {
          const isChecked = selectedDirections.includes(item!);

          return (
            <CheckboxListItem key={`${item}-${idx}`}>
              <label>
                <input
                  hidden
                  type="checkbox"
                  value={`${isChecked}`}
                  onChange={() => {
                    const newDirections = isChecked
                      ? selectedDirections.filter(
                          (direction) => direction !== item
                        )
                      : [...selectedDirections, item!];

                    router.push({
                      query: {
                        d: newDirections,
                        c: router.query.c,
                      },
                    });
                  }}
                />
                <CheckboxListInput isChecked={isChecked} />
                <span>{item}</span>
              </label>
            </CheckboxListItem>
          );
        })}
      </CheckboxList>
    </>
  );
};

const CategoriesList: React.FC<Pick<ConnectorsPageProps, 'categories'>> = ({
  categories,
}) => {
  const router = useRouter();
  const selectedCategories = parseSelectedCategories(router.query);

  return (
    <>
      <div className="mobile-only">
        <SelectInput
          label="Category"
          onChange={(e) => {
            router.push({
              query: {
                c: [e.target.value],
                d: router.query.d,
              },
            });
          }}
        >
          <option value="">Select a category</option>

          {categories?.map((item) => {
            return (
              <option key={item?.title} value={item?.system?.uid!}>
                {item?.title}
              </option>
            );
          })}
        </SelectInput>
      </div>

      <CheckboxList className="desktop-only">
        {categories?.map((item, idx) => {
          const isChecked = selectedCategories.includes(item?.system?.uid!);

          return (
            <CheckboxListItem key={`${item?.title}-${idx}`}>
              <label>
                <input
                  hidden
                  type="checkbox"
                  value={`${isChecked}`}
                  onChange={() => {
                    const newCategories = isChecked
                      ? selectedCategories.filter(
                          (category) => category !== item?.system?.uid
                        )
                      : [...selectedCategories, item?.system?.uid!];

                    router.push({
                      query: {
                        c: newCategories,
                        d: router.query.d,
                      },
                    });
                  }}
                />
                <CheckboxListInput isChecked={isChecked} />
                <span>{item?.title}</span>
              </label>
            </CheckboxListItem>
          );
        })}
      </CheckboxList>
    </>
  );
};

export const ConnectorsGrid: React.FC<
  Pick<ConnectorsPageProps, 'connectors'>
> = ({ connectors }) => {
  const columnGutter = '12px';
  const theme = getTheme();

  return (
    <Row style={{ margin: `-${columnGutter}`, width: 'auto' }}>
      {connectors?.map((item) => {
        const logo = item?.logoConnection?.edges?.find(
          (edge) => edge?.node?.url
        );

        return (
          <Col
            md={6}
            key={`${item?.url}-${item?.title}`}
            style={{
              padding: columnGutter,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Link
              href={item?.url || ''}
              style={{
                flex: '1 1 auto',
              }}
            >
              <ConnectorCard>
                <ConnectorCardLogo>
                  <OptimizedImage
                    loading="lazy"
                    src={logo?.node?.url || ''}
                    alt={item?.title || ''}
                    width={80}
                    height={80}
                  />
                </ConnectorCardLogo>

                <ConnectorCardContent>
                  <ConnectorCardTitle>
                    <span>{item?.title}</span>
                    <span style={{ opacity: 0.5, fontSize: '11px' }}>
                      {item?.direction}
                    </span>
                  </ConnectorCardTitle>

                  <ConnectorCardDescription>
                    {truncate(renderRichText(item?.mapped_description, true), {
                      length: 130,
                    })}
                  </ConnectorCardDescription>

                  <ConnectorCardTags>
                    {!!item?.availability &&
                      item?.availability?.toLowerCase() !==
                        'general availability' && (
                        <Tooltip
                          title="This connector will be publicly available soon"
                          placement="top"
                          arrow={true}
                        >
                          <ConnectorCardTag
                            style={{
                              color: theme.colors.eb2,
                            }}
                          >
                            {item?.availability}
                          </ConnectorCardTag>
                        </Tooltip>
                      )}

                    {!!item?.edge_y_n && (
                      <Tooltip
                        title="Requires Edge Gateway and Pro plan"
                        placement="top"
                        arrow={true}
                      >
                        <ConnectorCardTag
                          style={{ color: theme.colors.yellow }}
                        >
                          Edge
                        </ConnectorCardTag>
                      </Tooltip>
                    )}

                    {item?.categoriesConnection?.edges?.map((edge) => (
                      <ConnectorCardTag
                        key={`${item.title}-category-${edge?.node?.title}`}
                      >
                        {edge?.node?.title}
                      </ConnectorCardTag>
                    ))}
                  </ConnectorCardTags>
                </ConnectorCardContent>
              </ConnectorCard>
            </Link>
          </Col>
        );
      })}
    </Row>
  );
};

function parseSelectedCategories(query: ParsedUrlQuery) {
  if (!query.c) {
    return [];
  }

  return Array.isArray(query.c) ? query.c : [query.c];
}

function parseSelectedDirections(query: ParsedUrlQuery) {
  if (!query.d) {
    return [];
  }

  return Array.isArray(query.d) ? query.d : [query.d];
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    const selectedCategories = parseSelectedCategories(context.query);
    const selectedDirections = parseSelectedDirections(context.query);
    const selectedPage = Number(context.query.p || 1);

    // fetch page data and connectors at once
    const [{ data: pageData }, connectors] = await Promise.all([
      ContentStackClient().query<IPageProps<ConnectorsPage>>({
        query: PAGE_QUERY,
      }),

      fetchConnectors(),
    ]);

    // get all unique categories used by all connectors
    const categories = getCategoriesFromConnectors(connectors!);

    // filter connectors by directions, if given
    let filteredConnectors = connectors?.filter((connector) => {
      if (!selectedDirections?.length) {
        return true;
      }

      if (selectedDirections?.includes(connector?.direction!)) {
        return true;
      }

      return false;
    });

    // filter connectors by categories, if given
    filteredConnectors = filteredConnectors?.filter((connector) => {
      if (!selectedCategories?.length) {
        return true;
      }

      for (let category of connector?.categoriesConnection?.edges!) {
        if (selectedCategories?.includes(category?.node?.system?.uid!)) {
          return true;
        }
      }

      return false;
    });

    // store total amount of connectors before pagination
    const totalConnectors = filteredConnectors?.length;

    // paginate connectors
    const paginatedConnectors = filteredConnectors?.slice(
      (selectedPage - 1) * PAGE_SIZE,
      selectedPage * PAGE_SIZE
    );

    return {
      props: {
        ...pageData,
        categories,
        totalConnectors,
        connectors: paginatedConnectors,
      },
    };
  } catch (e) {
    return { props: {} };
  }
};

const PAGE_QUERY = gql`
  query connectorsPageQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}

    page: connectors_page(uid: "blt3656d1da6339b9ba") {
      title
      description
      hero {
        title
        text
      }
    }    
  }
`;

export default Page;
